// AlertDialog is a thin alias around the shared Dialog primitives so that
// destructive-confirmation dialogs (delete AI agent, delete API action,
// delete article, …) share the exact same visual shell as the rest of the
// app's modals (the one used by ApiAction, ShopifyMcpAction, etc.). The
// AlertDialog* export names are kept unchanged for backward compatibility
// with existing consumers.
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  HTMLAttributes,
  type ButtonHTMLAttributes,
} from 'react';
import { buttonVariants } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { cn } from '@/Domain/utils/cn';

const AlertDialog = Dialog;
const AlertDialogTrigger = DialogTrigger;
const AlertDialogPortal = DialogPortal;
const AlertDialogOverlay = DialogOverlay;

const AlertDialogContent = forwardRef<
  ElementRef<typeof DialogContent>,
  ComponentPropsWithoutRef<typeof DialogContent>
>((props, ref) => (
  // role="alertdialog" + aria-modal preserve the destructive-confirmation
  // semantics that we lost when AlertDialog stopped wrapping
  // @radix-ui/react-alert-dialog. Screen readers announce these as
  // alert-style modals (interrupting flow, requiring explicit response)
  // rather than generic dialogs.
  <DialogContent ref={ref} role="alertdialog" aria-modal="true" {...props} />
));
AlertDialogContent.displayName = 'AlertDialogContent';

const AlertDialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-4 text-left', className)} {...props} />
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

const AlertDialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-1 sm:flex-row sm:items-center sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogTitle = forwardRef<
  ElementRef<typeof DialogTitle>,
  ComponentPropsWithoutRef<typeof DialogTitle>
>(({ className, ...props }, ref) => (
  <DialogTitle ref={ref} className={cn('text-lg font-bold', className)} {...props} />
));
AlertDialogTitle.displayName = 'AlertDialogTitle';

const AlertDialogDescription = DialogDescription;

// Auto-close on click is the original AlertDialogPrimitive.Action behavior;
// preserve it by wrapping in DialogClose. Default styling matches the
// previous danger button.
const AlertDialogAction = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, type = 'button', ...props }, ref) => (
    <DialogClose asChild>
      <button
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant: 'danger', size: 'sm' }), className)}
        {...props}
      />
    </DialogClose>
  ),
);
AlertDialogAction.displayName = 'AlertDialogAction';

// Mirror the previous AlertDialogPrimitive.Cancel: when used with `asChild`,
// the consumer's button becomes the close trigger. Otherwise renders a
// default <button> with secondary styling.
type AlertDialogCancelProps = ComponentPropsWithoutRef<typeof DialogClose>;

const AlertDialogCancel = forwardRef<HTMLButtonElement, AlertDialogCancelProps>(
  ({ asChild, ...props }, ref) => <DialogClose asChild={asChild ?? false} ref={ref} {...props} />,
);
AlertDialogCancel.displayName = 'AlertDialogCancel';

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
